import { Transition } from "@headlessui/react";
import { BellIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";

import { ApiService } from "../../api/services.gen";

export default function Notifications({ onClose }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        ApiService.apiLeagueNotificationsRetrieve()
            .then((res) => {
                if (!cancelled) setNotifications(res?.notifications || []);
            })
            .catch((error) => {
                console.error("Error fetching notifications:", error);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleMarkAllRead = () => {
        ApiService.apiLeagueNotificationsReadAllCreate()
            .then(() => {
                setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            })
            .catch((error) => {
                console.error("Error marking notifications read:", error);
            });
    };

    const hasUnread = notifications.some((n) => !n.is_read);

    return (
        <div className="p-6 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <BellIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </div>

            {hasUnread && (
                <button
                    onClick={handleMarkAllRead}
                    className="self-end mb-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                    Mark all as read
                </button>
            )}

            {/* Divider */}
            <hr className="border-blue-200 dark:border-gray-700 mb-6" />

            {/* Notifications Content */}
            <Transition
                show={true}
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center flex-grow text-center">
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-grow text-center">
                        <BellIcon className="w-12 h-12 text-blue-300 dark:text-blue-700 mb-4" />
                        <p className="text-gray-500 dark:text-gray-400 text-sm">You have no new notifications.</p>
                    </div>
                ) : (
                    <div className="space-y-4 overflow-y-auto">
                        {notifications.map((n) => (
                            <div
                                key={n.id}
                                className={`p-3 rounded-lg border ${
                                    n.is_read
                                        ? "border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
                                        : "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20"
                                }`}
                            >
                                <p className="text-sm text-gray-900 dark:text-gray-100">{n.message}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {new Date(n.created_at).toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </Transition>
        </div>
    );
}
